/**
 * SidebarBuilderTab - Main sidebar builder component
 *
 * Three-panel drag-and-drop interface for configuring sidebar layout
 * per UI profile. Replaces separate SidebarCustomizerView.
 *
 * Layout: [Feature Pool | Sidebar Canvas | Live Preview]
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Save, RefreshCw, ChevronDown, ChevronRight, Code, AlertCircle } from 'lucide-react';
import { useAppFeatures } from '../../hooks/useAppFeatures.js';
import { useSidebarPreview } from '../../hooks/useSidebarPreview.js';
import { uiProfileAPI } from '../../../api.js';
import { sidebarAPI } from '../../../api/admin.js';
import { useProfile } from '../../context/ProfileContext.jsx';
import { SidebarBuilderDndContext } from './sidebar-builder/SidebarBuilderDndContext.jsx';
import { FeaturePool } from './sidebar-builder/FeaturePool.jsx';
import { SidebarCanvas } from './sidebar-builder/SidebarCanvas.jsx';
import { LiveSidebarPreview } from './sidebar-builder/LiveSidebarPreview.jsx';
import {
  profileDataToBuilderState,
  builderStateToProfileData,
  generateCategoryId,
} from './sidebar-builder/builderUtils.js';

// Scope type options for advanced override
const SCOPE_TYPES = [
  { id: 'GLOBAL', label: 'Global' },
  { id: 'TENANT', label: 'Tenant' },
  { id: 'PROFILE', label: 'Profil' },
  { id: 'USER', label: 'Utilisateur' },
];

export function SidebarBuilderTab({ profiles }) {
  const { features, groups, isLoading: featuresLoading } = useAppFeatures();
  const { resolvedGroups, warnings, isResolving, error: resolveError, resolve } = useSidebarPreview();
  const { refreshProfiles } = useProfile();

  // Selected profile
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);

  // Builder state
  const [builderState, setBuilderState] = useState({ categories: [], unassigned: [], pool: [] });
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Advanced mode
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advancedJson, setAdvancedJson] = useState('');
  const [advancedScope, setAdvancedScope] = useState('PROFILE');

  // Initialize with first active profile
  useEffect(() => {
    if (!selectedProfileId && profiles?.length > 0) {
      const active = profiles.find(p => p.status === 'active');
      if (active) {
        setSelectedProfileId(active.id);
      }
    }
  }, [profiles, selectedProfileId]);

  // Load profile and build initial state
  useEffect(() => {
    if (!selectedProfileId || !profiles || featuresLoading) return;

    const profile = profiles.find(p => p.id === selectedProfileId);
    if (!profile) return;

    setSelectedProfile(profile);

    // Resolve sidebar for this profile to get current layout
    const loadState = async () => {
      try {
        const res = await sidebarAPI.resolve({
          profileCode: profile.code,
          mode: 'elusive',
        });

        const resolved = res.ok ? (res.data?.groups || res.data) : null;
        const state = profileDataToBuilderState(profile, resolved, features);
        setBuilderState(state);
        setIsDirty(false);

        // Generate initial advanced JSON
        const { override } = builderStateToProfileData(state, groups);
        setAdvancedJson(JSON.stringify(override || {}, null, 2));
      } catch (err) {
        console.error('[SidebarBuilder] Failed to resolve:', err);
        // Fallback: build state without resolver
        const state = profileDataToBuilderState(profile, null, features);
        setBuilderState(state);
        setIsDirty(false);
      }
    };

    loadState();
  }, [selectedProfileId, profiles, features, featuresLoading, groups]);

  // Trigger live preview when builder state changes
  useEffect(() => {
    if (!selectedProfile || !isDirty) return;

    const { override } = builderStateToProfileData(builderState, groups);

    resolve({
      profileCode: selectedProfile.code,
      mode: 'elusive',
      override: override || undefined,
    });

    // Update advanced JSON
    setAdvancedJson(JSON.stringify(override || {}, null, 2));
  }, [builderState, isDirty, selectedProfile, groups, resolve]);

  // Handle state changes from DnD
  const handleStateChange = useCallback((newState) => {
    setBuilderState(newState);
    setIsDirty(true);
    setSaveSuccess(false);
  }, []);

  // Remove item from canvas → return to pool
  const handleRemoveItem = useCallback((featureId) => {
    setBuilderState(prev => {
      const newState = {
        categories: prev.categories.map(c => ({
          ...c,
          items: c.items.filter(id => id !== featureId),
        })),
        unassigned: (prev.unassigned || []).filter(id => id !== featureId),
        pool: [...(prev.pool || []), featureId],
      };
      return newState;
    });
    setIsDirty(true);
    setSaveSuccess(false);
  }, []);

  // Rename category
  const handleRenameCategory = useCallback((catId, newLabel) => {
    setBuilderState(prev => ({
      ...prev,
      categories: prev.categories.map(c =>
        c.id === catId ? { ...c, label: newLabel } : c
      ),
    }));
    setIsDirty(true);
    setSaveSuccess(false);
  }, []);

  // Delete empty category
  const handleDeleteCategory = useCallback((catId) => {
    setBuilderState(prev => ({
      ...prev,
      categories: prev.categories.filter(c => c.id !== catId),
    }));
    setIsDirty(true);
    setSaveSuccess(false);
  }, []);

  // Add new category
  const handleAddCategory = useCallback(() => {
    const newCat = {
      id: generateCategoryId(),
      code: `custom_${Date.now()}`,
      label: 'Nouveau groupe',
      items: [],
    };
    setBuilderState(prev => ({
      ...prev,
      categories: [...prev.categories, newCat],
    }));
    setIsDirty(true);
    setSaveSuccess(false);
  }, []);

  // Save — saves both modules (which features are visible) and layout override
  // (group order, item order, group renaming). The builder is the visual
  // source of truth: items placed in categories = active modules.
  //
  // Uses full_layout format which bypasses the legacy module-based filtering.
  // This means the builder defines the COMPLETE sidebar — the modal's module
  // checkboxes no longer condition which categories appear.
  const handleSave = async () => {
    if (!selectedProfile) return;

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const { modules, override } = builderStateToProfileData(builderState, groups);

      // 1. Sync modules to profile (items in categories = active modules)
      if (modules && modules.length > 0) {
        await uiProfileAPI.update(selectedProfile.id, { modules });
      }

      // 2. Save layout override (upsert: update existing or create new)
      if (override && Object.keys(override).length > 0) {
        const scopeType = advancedScope;
        const scopeKey = scopeType === 'PROFILE' ? selectedProfile.code : 'default';

        // Check for existing overrides for this scope
        const existing = await sidebarAPI.getOverrides({
          scopeType,
          scopeKey,
        });

        if (existing.ok && existing.data && existing.data.length > 0) {
          // Update the latest override (replaces legacy format with full_layout)
          const latest = existing.data[0];
          await sidebarAPI.updateOverride(latest.id, {
            override_json: override,
            name: `Builder: ${selectedProfile.name}`,
            description: `WYSIWYG layout for profile ${selectedProfile.code}`,
            is_enabled: true,
          });

          // Disable older duplicates (keep only the latest)
          for (let i = 1; i < existing.data.length; i++) {
            await sidebarAPI.updateOverride(existing.data[i].id, {
              is_enabled: false,
            });
          }
        } else {
          // No existing override — create new
          await sidebarAPI.createOverride({
            scope_type: scopeType,
            scope_key: scopeKey,
            mode: 'any',
            name: `Builder: ${selectedProfile.name}`,
            description: `WYSIWYG layout for profile ${selectedProfile.code}`,
            override_json: override,
            is_enabled: true,
          });
        }
      }

      // 3. Refresh profile context so sidebar picks up changes
      await refreshProfiles();

      setIsDirty(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('[SidebarBuilder] Save failed:', err);
      setSaveError(err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (featuresLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Top bar: Profile selector + Save */}
      <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 p-4 flex items-center gap-4">
        <div className="flex items-center gap-2 flex-1">
          <label className="text-sm text-gray-500 dark:text-slate-400 whitespace-nowrap">
            Profil :
          </label>
          <select
            value={selectedProfileId || ''}
            onChange={e => {
              setSelectedProfileId(Number(e.target.value));
              setIsDirty(false);
            }}
            className="px-3 py-1.5 bg-gray-100 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {(profiles || []).map(p => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.code})
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-3">
          {saveError && (
            <span className="text-xs text-red-500 flex items-center gap-1">
              <AlertCircle size={12} />
              {saveError}
            </span>
          )}
          {saveSuccess && (
            <span className="text-xs text-green-500">Sauvegardé</span>
          )}
          <button
            onClick={handleSave}
            disabled={!isDirty || saving}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors
              ${isDirty
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-200 dark:bg-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed'
              }`}
          >
            <Save size={16} />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {/* Three-panel builder */}
      <div className="grid grid-cols-[250px_1fr_280px] gap-4 min-h-[500px]">
        <SidebarBuilderDndContext
          state={builderState}
          onStateChange={handleStateChange}
          allFeatures={features}
        >
          {/* Left: Feature Pool */}
          <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
            <FeaturePool
              poolItems={builderState.pool || []}
              allFeatures={features}
              groups={groups}
            />
          </div>

          {/* Center: Sidebar Canvas */}
          <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
            <SidebarCanvas
              state={builderState}
              allFeatures={features}
              onRemoveItem={handleRemoveItem}
              onRenameCategory={handleRenameCategory}
              onDeleteCategory={handleDeleteCategory}
              onAddCategory={handleAddCategory}
            />
          </div>

          {/* Right: Live Preview */}
          <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
            <LiveSidebarPreview
              resolvedGroups={resolvedGroups}
              warnings={warnings}
              isResolving={isResolving}
              error={resolveError}
              allFeatures={features}
              builderState={builderState}
            />
          </div>
        </SidebarBuilderDndContext>
      </div>

      {/* Advanced: Collapsible Override JSON */}
      <div className="bg-white dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full px-4 py-3 flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
        >
          {showAdvanced ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <Code size={16} />
          Mode avancé — Override JSON
        </button>

        {showAdvanced && (
          <div className="px-4 pb-4 space-y-3 border-t border-gray-200 dark:border-slate-700 pt-3">
            <div className="flex items-center gap-4">
              <div>
                <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">Portée</label>
                <select
                  value={advancedScope}
                  onChange={e => setAdvancedScope(e.target.value)}
                  className="px-2 py-1 bg-gray-100 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded text-xs text-gray-700 dark:text-slate-300 focus:outline-none focus:ring-1 focus:ring-purple-500"
                >
                  {SCOPE_TYPES.map(s => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="text-xs text-gray-400 dark:text-slate-500 flex-1">
                JSON généré automatiquement depuis le constructeur visuel.
                Priorité : USER &gt; PROFILE &gt; TENANT &gt; GLOBAL
              </div>
            </div>

            <textarea
              value={advancedJson}
              readOnly
              rows={10}
              spellCheck={false}
              className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-600 rounded-lg text-xs font-mono text-gray-700 dark:text-slate-300 resize-y focus:outline-none"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default SidebarBuilderTab;
